import React from 'react';
import P from 'prop-types';
// eslint-disable-next-line lodash/import-scope
import property from 'lodash/property';
import { Box, Text } from '@nerdwallet/currency';
import Image from '@nerdwallet/react-image';

import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import IframeResizer from 'iframe-resizer-react';
import {
  group,
  stringInputSpec,
  stringSelectSpec,
  stringTextAreaSpec,
} from '../../lib/editing-configs';

import UniversalContainer, {
  universalContainerPropTypes,
} from '../universal-container';
import UniversalText from '../universal-text/universal-text';
import styles from './third-party-embed-v2.module.less';

const isValidUrl = (url) => !!url.match(/^(http|https):\/\//);

const scriptEmbedContent = (scriptSrc) => {
  const htmlSnippet = `
    <script
      src="${scriptSrc}">
    </script>
  `;
  if (!scriptSrc) {
    return null;
  }
  return (
    <span
      id="script-embed-container"
      style={{ textAlign: 'left' }}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: htmlSnippet }}
    />
  );
};

const iframeEmbedContent = (iframeSrc, height, width) => {
  return (
    isValidUrl(iframeSrc) && (
      <Box id="iframe-wrapper" className={styles.iframeBox}>
        <IframeResizer
          src={iframeSrc}
          style={{ minWidth: width || '100%', height: height || '100%' }}
        />
      </Box>
    )
  );
};

const renderContent = (embedType, iframeSrc, scriptSrc, height, width) => {
  const type = {
    iframe: iframeEmbedContent(iframeSrc, height, width),
    script: scriptEmbedContent(scriptSrc),
  };

  if (
    (embedType === 'script' && !scriptSrc) ||
    (embedType === 'iframe' && !iframeSrc)
  ) {
    return (
      <Box id="default-empty-src-message" style={{ margin: '2rem 0' }}>
        <Text size="large" bold>
          Enter a {embedType} URL to make the content appear
        </Text>
      </Box>
    );
  }

  return type[embedType];
};

const getAlignment = (alignment) => {
  const type = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
  };
  return type[alignment];
};

const ThirdPartyEmbedV2 = ({
  src,
  scriptSrc,
  embedType,
  height,
  heightMobile,
  width,
  widthMobile,
  cardIcon,
  title,
  subtitle,
  alignment,
  container,
  cardContainer,
  size,
}) => {
  const heightValue = (size !== 'lg' && heightMobile) || height;
  const withValue = (size !== 'lg' && widthMobile) || width;
  return (
    <UniversalContainer {...container}>
      <Box
        className={styles.container}
        style={{ alignItems: getAlignment(alignment) }}
      >
        <UniversalContainer {...cardContainer}>
          <Box className={styles.card}>
            {cardIcon && (
              <Image
                objectFit="contain"
                style={{ maxHeight: 90, width: 'auto' }}
                src={cardIcon}
                alt="NerdWallet Brand"
                backgroundColor="transparent"
                naturalHeight={90}
                naturalWidth={221}
              />
            )}
            <Box className={styles.cardHeading}>
              {title && <UniversalText {...title} />}
              {subtitle && (
                <UniversalText
                  {...subtitle}
                  defaultOptions={{ text: { textType: 'text' } }}
                />
              )}
            </Box>
            {renderContent(embedType, src, scriptSrc, heightValue, withValue)}
          </Box>
        </UniversalContainer>
      </Box>
    </UniversalContainer>
  );
};

ThirdPartyEmbedV2.propTypes = {
  embedType: P.oneOf(['script', 'iframe']),
  alignment: P.oneOf(['left', 'center', 'right']),
  cardIcon: P.string,
  container: universalContainerPropTypes,
  cardContainer: universalContainerPropTypes,
  title: UniversalText.propTypes,
  subtitle: UniversalText.propTypes,
  children: P.node,
  src: P.string,
  scriptSrc: P.string,
  formTitle: P.string,
  height: P.string,
  heightMobile: P.string,
  width: P.string,
  widthMobile: P.string,
  size: P.string,
  disableTopBottomCardColor: P.bool,
};

ThirdPartyEmbedV2.defaultProps = {
  src: '',
  scriptSrc: '',
  formTitle: '',
  embedType: 'iframe',
  height: '100%',
  width: '100%',
  disableTopBottomCardColor: false,
};

ThirdPartyEmbedV2.getEditingConfig = (componentDocument) => {
  const embedTypeInputConfig =
    componentDocument?.props?.embedType === 'script'
      ? [stringTextAreaSpec('scriptSrc', 'Script Source URL')]
      : [
          stringInputSpec('src', 'Iframe Source URL'),
          stringInputSpec('height', 'Iframe Height', {
            help: 'Defaults to "100%"',
          }),
          stringInputSpec('heightMobile', 'Iframe Height Mobile'),
          stringInputSpec('width', 'Iframe Width', {
            help: 'Defaults to "100%"',
          }),
          stringInputSpec('widthMobile', 'Iframe Width Mobile'),
        ];
  return [
    ...UniversalContainer.getEditingConfig(
      componentDocument,
      'Container',
      [
        'backgroundColor',
        'backgroundImage',
        'border',
        'margin',
        'padding',
        'hide',
        'jumpLinkId',
      ],
      'container'
    ),
    group('Card Container', [
      stringInputSpec('cardIcon', 'Card Icon'),
      ...UniversalContainer.getEditingConfig(
        componentDocument,
        'Card Container',
        ['border', 'margin', 'padding', 'backgroundColor', 'backgroundImage'],
        'cardContainer',
        { disableGroup: true }
      ),
    ]),
    ...UniversalText.getEditingConfig(
      componentDocument,
      'Title',
      [
        'fontWeight',
        'groupButton',
        'margin',
        'padding',
        'fontFamily',
        'textColor',
        'textSize',
        'textType',
      ],
      'title'
    ),
    ...UniversalText.getEditingConfig(
      componentDocument,
      'Subtitle',
      [
        'fontWeight',
        'groupButton',
        'margin',
        'padding',
        'fontFamily',
        'textColor',
        'textSize',
        'textType',
      ],
      'subtitle',
      { text: { textType: 'text' } }
    ),

    group('Third Party Embed', [
      stringSelectSpec(
        `embedType`,
        'Embed Type',
        [
          { label: 'Iframe', value: 'iframe' },
          { label: 'Script', value: 'script' },
        ],
        {
          help: 'Defaults to "Iframe"',
        }
      ),
      stringSelectSpec(
        `alignment`,
        'Content Alignment',
        [
          { label: 'Left', value: 'left' },
          { label: 'Center', value: 'center' },
          { label: 'Right', value: 'right' },
        ],
        {
          help: 'Defaults to "Center"',
        }
      ),
      ...embedTypeInputConfig,
    ]),
  ];
};

ThirdPartyEmbedV2.displayName = 'ThirdPartyEmbedV2';
export default connect(
  createStructuredSelector({
    size: property('mediaQuery'),
  })
)(ThirdPartyEmbedV2);
